import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ClipboardText, CheckCircle, FilePdf, FileXls, PencilSimple, Wind, Warning, X, Camera, IdentificationBadge, CalendarBlank, Signature } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import PhotoUploader from "@/components/PhotoUploader";
import EmployeePicker from "@/components/EmployeePicker";
import SignaturePad from "@/components/SignaturePad";
import DailyReportThemePanel from "@/components/hse/DailyReportThemePanel";
import QuickSignButton from "@/components/hse/QuickSignButton";

const emptyIncident = (n) => ({
  number: n, description: "", kind: "condicion", area: "",
  person_involved: "", person_employee_id: "", action_taken: "",
});
const emptyEnv = () => ({
  lugar: "", hora: "", poe: "", distancia_manga: "",
  co: "", no2: "", o2: "", temperatura_c: "", temperatura_ltgbh: "",
  humedad: "", velocidad_viento: "",
});
const emptyForm = () => ({
  date: "", project_id: defaultProject(), shift: "diurno", supervisor: "", supervisor_employee_id: "",
  supervisor_signature: "",
  dsai: "", accidents_reported: 0, personal_direct: 0, personal_indirect: 0,
  accidents_description: "",
  talk_title: "", talk_duration_min: 15, talk_instructor: "",
  talk_instructor_employee_id: "", talk_participants: 0,
  activities_safety: [], activities_operation: [],
  incidents: [], env_readings: [],
  photos: [],
  photos_charla: [],
  photos_actividades: [],
  photos_incidencias: [],
  status: "open",
});

/**
 * DailyReportModule — captura del "Reporte Diario de Seguridad, Salud y
 * Medio Ambiente" formato Silverco-Mafrisa. Al imprimir PDF entrega el
 * mismo layout del Excel original.
 */
export default function DailyReportModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  // Texto crudo de los textareas — evita perder saltos de línea mientras se edita
  const [safetyText, setSafetyText] = useState("");
  const [operationText, setOperationText] = useState("");
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    // Iter120: usar endpoint /lite (sin base64 de fotos) para que la tabla
    // aparezca en <1s. Las fotos se descargan bajo demanda al abrir/editar
    // un reporte específico.
    api.get("/hse/daily-reports/lite").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (it) => {
    setEditingId(it.id);
    // Iter120: la lista es lite → traemos el reporte completo con fotos al editar.
    try {
      const { data: full } = await api.get(`/hse/daily-reports/${it.id}`);
      setForm({ ...emptyForm(), ...full });
      setSafetyText((full.activities_safety || []).join("\n"));
      setOperationText((full.activities_operation || []).join("\n"));
    } catch (_e) {
      setForm({ ...emptyForm(), ...it });
      setSafetyText((it.activities_safety || []).join("\n"));
      setOperationText((it.activities_operation || []).join("\n"));
    }
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!assertProject(form.project_id)) return;
      const payload = {
        ...form,
        activities_safety: safetyText.split("\n").map((s) => s.trim()).filter(Boolean),
        activities_operation: operationText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) await api.put(`/hse/daily-reports/${editingId}`, payload);
      else await api.post("/hse/daily-reports", payload);
      toast.success("Reporte guardado");
      setOpen(false); setEditingId(null); setForm(emptyForm());
      setSafetyText(""); setOperationText("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar reporte diario?")) return;
    try { await api.delete(`/hse/daily-reports/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const downloadPdf = async (id, date, shift) => {
    try {
      const { data } = await api.get(`/hse/daily-reports/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reporte-diario-${date || "sd"}-${shift || "sd"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadXlsx = async (id, date, shift) => {
    try {
      const { data } = await api.get(`/hse/daily-reports/${id}/xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reporte-diario-${date || "sd"}-${shift || "sd"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadWeekly = async () => {
    // Iter134c · pide fecha de lunes al usuario (por defecto semana actual)
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const defaultIso = monday.toISOString().slice(0, 10);
    const picked = window.prompt("Semana (lunes YYYY-MM-DD):", defaultIso);
    if (picked === null) return;
    const wk = (picked || defaultIso).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(wk)) {
      toast.error("Fecha inválida — usa YYYY-MM-DD");
      return;
    }
    try {
      const { data } = await api.get("/hse/daily-reports/weekly/pdf", {
        params: { week_start: wk }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reporte-diario-semanal-${wk}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Consolidado semanal generado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadMonthly = async () => {
    // Iter134c · consolidado mensual (faltaba el botón en la UI)
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const picked = window.prompt("Mes (YYYY-MM):", defaultMonth);
    if (picked === null) return;
    const mm = (picked || defaultMonth).trim();
    if (!/^\d{4}-\d{2}$/.test(mm)) {
      toast.error("Mes inválido — usa YYYY-MM");
      return;
    }
    try {
      const { data } = await api.get("/hse/daily-reports/monthly/pdf", {
        params: { month: mm }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reporte-diario-mensual-${mm}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Consolidado mensual generado");
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- Sub-form helpers ----
  const addIncident = () => setForm({ ...form, incidents: [...form.incidents, emptyIncident(form.incidents.length + 1)] });
  const updIncident = (idx, k, v) => {
    const n = [...form.incidents]; n[idx] = { ...n[idx], [k]: v };
    setForm({ ...form, incidents: n });
  };
  const linkIncidentPerson = (idx, emp) => {
    const n = [...form.incidents];
    n[idx] = { ...n[idx], person_involved: emp.name, person_employee_id: emp.id };
    setForm({ ...form, incidents: n });
  };
  const addEnv = () => setForm({ ...form, env_readings: [...form.env_readings, emptyEnv()] });
  const updEnv = (idx, k, v) => {
    const n = [...form.env_readings]; n[idx] = { ...n[idx], [k]: v };
    setForm({ ...form, env_readings: n });
  };
  const pickSupervisor = (emp) => setForm({ ...form, supervisor: emp.name, supervisor_employee_id: emp.id });
  const pickInstructor = (emp) => setForm({ ...form, talk_instructor: emp.name, talk_instructor_employee_id: emp.id });

  const rows = useMemo(() => items.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")), [items]);

  return (
    <div className="space-y-4" data-testid="daily-report-module">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-slate-600">{items.length} reportes diarios registrados</p>
          <p className="text-[10px] text-slate-400 italic">Formato Silverco-Mafrisa · Un reporte por (fecha, turno)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["admin", "manager"].includes(user?.role) && (
            <DailyReportThemePanel canManage={true} />
          )}
          <Button data-testid="daily-weekly-button" variant="outline" onClick={downloadWeekly} className="gap-1 h-9 bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100">
            <CalendarBlank size={14} weight="bold" /> Consolidado semanal
          </Button>
          <Button data-testid="daily-monthly-button" variant="outline" onClick={downloadMonthly} className="gap-1 h-9 bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100">
            <CalendarBlank size={14} weight="bold" /> Consolidado mensual
          </Button>
          {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(emptyForm()); setSafetyText(""); setOperationText(""); } }}>
            <DialogTrigger asChild>
              <Button data-testid="daily-new-button" className="bg-orange-700 hover:bg-orange-800 gap-2"><Plus size={14} weight="bold" /> Nuevo reporte diario</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editingId ? "Editar" : "Nuevo"} — Reporte Diario de Seguridad, Salud y Medio Ambiente</DialogTitle></DialogHeader>

              {/* Cabecera */}
              <div className="grid grid-cols-4 gap-3 border border-slate-200 rounded p-3">
                <ProjectField value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="daily-project" />
                <div><Label className="overline-label">Fecha</Label>
                  <DateField testid="daily-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
                <div><Label className="overline-label">Turno</Label>
                  <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                    <SelectTrigger data-testid="daily-shift"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="diurno">Diurno</SelectItem><SelectItem value="nocturno">Nocturno</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="overline-label">Accidentes reportados</Label>
                  <Input type="number" data-testid="daily-accidents" value={form.accidents_reported} onChange={(e) => setForm({ ...form, accidents_reported: Number(e.target.value) || 0 })} /></div>
                <div><Label className="overline-label">DSAI</Label>
                  <Input data-testid="daily-dsai" value={form.dsai} onChange={(e) => setForm({ ...form, dsai: e.target.value })} placeholder="Ej: 28" /></div>
                <div className="col-span-2"><Label className="overline-label">Supervisor que reporta turno</Label>
                  {form.supervisor_employee_id ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 h-9" data-testid="daily-supervisor-linked">
                      <IdentificationBadge size={14} weight="fill" className="text-emerald-700" />
                      <p className="text-xs font-semibold text-slate-900 flex-1 truncate">{form.supervisor}</p>
                      <button onClick={() => setForm({ ...form, supervisor_employee_id: "" })} className="text-[10px] text-slate-500 hover:text-red-600 font-semibold">Editar</button>
                    </div>
                  ) : (
                    <>
                      <EmployeePicker onPick={pickSupervisor} testid="daily-supervisor-picker" placeholder="Buscar en nómina…" />
                      <Input className="mt-1 h-8 text-xs" placeholder="…o manual" value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} data-testid="daily-supervisor-input" />
                    </>
                  )}
                </div>
                <div><Label className="overline-label"># Personal directo</Label>
                  <Input type="number" data-testid="daily-direct" value={form.personal_direct} onChange={(e) => setForm({ ...form, personal_direct: Number(e.target.value) || 0 })} /></div>
                <div><Label className="overline-label"># Personal indirecto</Label>
                  <Input type="number" data-testid="daily-indirect" value={form.personal_indirect} onChange={(e) => setForm({ ...form, personal_indirect: Number(e.target.value) || 0 })} /></div>
                <div className="col-span-4"><Label className="overline-label">Descripción breve de accidentes / eventos</Label>
                  <Textarea rows={2} data-testid="daily-accidents-desc" value={form.accidents_description} onChange={(e) => setForm({ ...form, accidents_description: e.target.value })} placeholder="Describe brevemente si hubo accidentes/incidentes durante el turno" /></div>
              </div>

              {/* Plática de seguridad */}
              <div className="grid grid-cols-4 gap-3 border border-slate-200 rounded p-3 mt-3">
                <div className="col-span-4 text-xs font-bold uppercase tracking-widest text-orange-700 bg-orange-50 px-2 py-1 rounded">Compartir hoy (Plática de seguridad)</div>
                <div className="col-span-3"><Label className="overline-label">Título / PST</Label>
                  <Input data-testid="daily-talk-title" value={form.talk_title} onChange={(e) => setForm({ ...form, talk_title: e.target.value })} placeholder="Ej: PST INGRESO Y SALIDA DE MINA (TARJETERO)" /></div>
                <div><Label className="overline-label">Duración (min)</Label>
                  <Input type="number" data-testid="daily-talk-duration" value={form.talk_duration_min} onChange={(e) => setForm({ ...form, talk_duration_min: Number(e.target.value) || 0 })} /></div>
                <div className="col-span-3"><Label className="overline-label">Instructor</Label>
                  {form.talk_instructor_employee_id ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 h-9" data-testid="daily-instructor-linked">
                      <IdentificationBadge size={14} weight="fill" className="text-emerald-700" />
                      <p className="text-xs font-semibold text-slate-900 flex-1 truncate">{form.talk_instructor}</p>
                      <button onClick={() => setForm({ ...form, talk_instructor_employee_id: "" })} className="text-[10px] text-slate-500 hover:text-red-600 font-semibold">Editar</button>
                    </div>
                  ) : (
                    <>
                      <EmployeePicker onPick={pickInstructor} testid="daily-instructor-picker" placeholder="Buscar en nómina…" />
                      <Input className="mt-1 h-8 text-xs" placeholder="…o manual" value={form.talk_instructor} onChange={(e) => setForm({ ...form, talk_instructor: e.target.value })} data-testid="daily-instructor-input" />
                    </>
                  )}
                </div>
                <div><Label className="overline-label"># Participantes</Label>
                  <Input type="number" data-testid="daily-talk-participants" value={form.talk_participants} onChange={(e) => setForm({ ...form, talk_participants: Number(e.target.value) || 0 })} /></div>
              </div>

              {/* Actividades */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="border border-slate-200 rounded p-3">
                  <Label className="overline-label">Actividades realizadas — Seguridad</Label>
                  <Textarea rows={6} className="text-xs mt-1" placeholder="Un ítem por línea. Ej:&#10;RIT a inicio de turno&#10;AST cargado de barrenos&#10;Permiso de trabajo en alturas" value={safetyText} onChange={(e) => setSafetyText(e.target.value)} data-testid="daily-act-safety" />
                  <p className="text-[10px] text-slate-500 mt-1">{safetyText.split("\n").map((s) => s.trim()).filter(Boolean).length} ítem(s)</p>
                </div>
                <div className="border border-slate-200 rounded p-3">
                  <Label className="overline-label">Actividades realizadas — Operación / Mtto</Label>
                  <Textarea rows={6} className="text-xs mt-1" placeholder="Un ítem por línea. Ej:&#10;Asignación de puebles&#10;Cargado de barrenos con cemento expansivo&#10;Lavado de cresta con pipa de agua" value={operationText} onChange={(e) => setOperationText(e.target.value)} data-testid="daily-act-oper" />
                  <p className="text-[10px] text-slate-500 mt-1">{operationText.split("\n").map((s) => s.trim()).filter(Boolean).length} ítem(s)</p>
                </div>
              </div>

              {/* Detección / Incidencias */}
              <div className="border border-slate-200 rounded p-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="overline-label flex items-center gap-1"><Warning size={12} weight="bold" className="text-red-600" /> Detección de actos y condiciones</p>
                  <Button size="sm" variant="ghost" onClick={addIncident} data-testid="daily-add-incident" className="h-7 text-xs text-red-700"><Plus size={12} /> Añadir</Button>
                </div>
                <div className="space-y-2">
                  {form.incidents.map((inc, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1 bg-slate-50 rounded p-2 border border-slate-200" data-testid={`daily-inc-${idx}`}>
                      <Input className="col-span-1 h-8 text-xs text-center" value={inc.number} onChange={(e) => updIncident(idx, "number", Number(e.target.value) || (idx + 1))} />
                      <Textarea className="col-span-5 text-xs min-h-[32px]" rows={1} placeholder="Detección (qué se observó)" value={inc.description} onChange={(e) => updIncident(idx, "description", e.target.value)} data-testid={`daily-inc-desc-${idx}`} />
                      <Select value={inc.kind} onValueChange={(v) => updIncident(idx, "kind", v)}>
                        <SelectTrigger className="col-span-1 h-8 text-[10px]" data-testid={`daily-inc-kind-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="acto">ACTO</SelectItem><SelectItem value="condicion">CONDICIÓN</SelectItem></SelectContent>
                      </Select>
                      <Input className="col-span-2 h-8 text-xs" placeholder="Área" value={inc.area} onChange={(e) => updIncident(idx, "area", e.target.value)} />
                      <div className="col-span-2">
                        {inc.person_employee_id ? (
                          <div className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5 h-8" data-testid={`daily-inc-linked-${idx}`}>
                            <IdentificationBadge size={12} weight="fill" className="text-emerald-700 shrink-0" />
                            <p className="text-[10px] font-semibold text-slate-900 flex-1 truncate">{inc.person_involved}</p>
                            <button onClick={() => updIncident(idx, "person_employee_id", "")} className="text-[9px] text-slate-500 hover:text-red-600 font-semibold">×</button>
                          </div>
                        ) : (
                          <EmployeePicker onPick={(emp) => linkIncidentPerson(idx, emp)} testid={`daily-inc-picker-${idx}`} placeholder="Persona involucrada" />
                        )}
                      </div>
                      <button className="col-span-1 h-8 text-slate-400 hover:text-red-600 flex items-center justify-center" onClick={() => setForm({ ...form, incidents: form.incidents.filter((_, i) => i !== idx) })} data-testid={`daily-inc-remove-${idx}`}><Trash size={12} /></button>
                      <Textarea className="col-span-12 text-xs mt-1" rows={1} placeholder="Acción / Corrección (qué se hizo o qué documento se generó)" value={inc.action_taken} onChange={(e) => updIncident(idx, "action_taken", e.target.value)} data-testid={`daily-inc-action-${idx}`} />
                    </div>
                  ))}
                  {form.incidents.length === 0 && <p className="text-xs text-slate-400 italic text-center py-2">Sin incidencias registradas.</p>}
                </div>
              </div>

              {/* Ambiente */}
              <div className="border border-slate-200 rounded p-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="overline-label flex items-center gap-1"><Wind size={12} weight="bold" className="text-cyan-700" /> Condiciones ambientales del recorrido</p>
                  <Button size="sm" variant="ghost" onClick={addEnv} data-testid="daily-add-env" className="h-7 text-xs text-cyan-700"><Plus size={12} /> Añadir lectura</Button>
                </div>
                {form.env_readings.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]" data-testid="daily-env-table">
                      <thead className="bg-slate-100">
                        <tr>
                          {["Lugar", "Hora", "POE", "Dist. manga", "CO", "NO2", "O2", "T°C", "T ltgbh", "Humedad", "Viento", ""].map((h, i) => (
                            <th key={i} className="text-left px-1 py-1 font-bold uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {form.env_readings.map((r, idx) => (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`daily-env-row-${idx}`}>
                            {["lugar", "hora", "poe", "distancia_manga", "co", "no2", "o2", "temperatura_c", "temperatura_ltgbh", "humedad", "velocidad_viento"].map((k) => (
                              <td key={k} className="p-0.5"><Input className="h-7 text-[10px] px-1" value={r[k] || ""} onChange={(e) => updEnv(idx, k, e.target.value)} data-testid={`daily-env-${k}-${idx}`} /></td>
                            ))}
                            <td className="p-0.5"><button className="text-slate-400 hover:text-red-600" onClick={() => setForm({ ...form, env_readings: form.env_readings.filter((_, i) => i !== idx) })}><Trash size={12} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Fotos por sección — respeta el formato del Excel Silverco-Mafrisa */}
              <div className="border border-slate-200 rounded p-3 mt-3 space-y-3">
                <p className="text-xs font-bold uppercase tracking-widest text-orange-700 bg-orange-50 px-2 py-1 rounded flex items-center gap-1">
                  <Camera size={12} weight="bold" /> Registro fotográfico (por sección — como el Excel)
                </p>
                <div>
                  <Label className="overline-label text-slate-700">1) Charla / Compartir hoy (máx 6)</Label>
                  <PhotoUploader value={form.photos_charla || []} onChange={(v) => setForm({ ...form, photos_charla: v })} max={6} testidPrefix="daily-photo-charla" />
                </div>
                <div>
                  <Label className="overline-label text-slate-700">2) Actividades / operación (máx 8)</Label>
                  <PhotoUploader value={form.photos_actividades || []} onChange={(v) => setForm({ ...form, photos_actividades: v })} max={8} testidPrefix="daily-photo-act" />
                </div>
                <div>
                  <Label className="overline-label text-slate-700">3) Incidencias / actos y condiciones (máx 8)</Label>
                  <PhotoUploader value={form.photos_incidencias || []} onChange={(v) => setForm({ ...form, photos_incidencias: v })} max={8} testidPrefix="daily-photo-inc" />
                </div>
                <div>
                  <Label className="overline-label text-slate-700">4) Evidencia general del turno (máx 12)</Label>
                  <PhotoUploader value={form.photos || []} onChange={(photos) => setForm({ ...form, photos })} max={12} testidPrefix="daily-photo" />
                </div>
              </div>

              {/* Firma del supervisor */}
              <div className="border border-blue-200 rounded p-3 mt-3 bg-blue-50">
                <Label className="overline-label flex items-center gap-1 text-blue-800"><Signature size={12} weight="bold" /> Firma del supervisor (respaldo legal)</Label>
                <p className="text-[10px] text-slate-600 mb-1">Se embebe en el PDF con QR de validación. Puedes firmar ahora o después desde la edición del reporte.</p>
                <div className="bg-white p-2 rounded border border-slate-300">
                  <SignaturePad value={form.supervisor_signature} onChange={(sig) => setForm({ ...form, supervisor_signature: sig })} width={480} height={140} testid="daily-supervisor-signature" />
                </div>
              </div>

              <Button data-testid="daily-save" onClick={save} disabled={!form.date || !form.supervisor} className="bg-orange-700 hover:bg-orange-800 w-full mt-2">{editingId ? "Actualizar" : "Crear"} reporte</Button>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-lg">Sin reportes diarios registrados</p>}
        {rows.map((it) => (
          <div key={it.id} data-testid={`daily-card-${it.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <ClipboardText size={18} weight="duotone" className="text-orange-700" />
                <div className="min-w-0">
                  <p className="font-heading font-bold text-slate-900 truncate">{it.date} · Turno {it.shift} <ProjectBadge id={it.project_id} className="ml-1 align-middle" /></p>
                  <p className="text-[11px] text-slate-500 truncate">Supervisor: {it.supervisor || "-"} · DSAI {it.dsai || "-"} · {it.personal_direct || 0} directos · {it.personal_indirect || 0} indirectos</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`daily-card-acc-${it.id}`}>{it.accidents_reported || 0} accidentes</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{(it.incidents || []).length} incidencias</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">{(it.env_readings || []).length} lecturas amb.</span>
                {(it.photos || []).length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 flex items-center gap-1"><Camera size={10} weight="fill" /> {it.photos.length}</span>}
                <Button size="sm" variant="outline" data-testid={`daily-pdf-${it.id}`} onClick={() => downloadPdf(it.id, it.date, it.shift)} className="h-7 text-xs gap-1 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"><FilePdf size={12} weight="bold" /> PDF</Button>
                <Button size="sm" variant="outline" data-testid={`daily-xlsx-${it.id}`} onClick={() => downloadXlsx(it.id, it.date, it.shift)} className="h-7 text-xs gap-1 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"><FileXls size={12} weight="bold" /> XLSX</Button>
                {canWrite && <QuickSignButton report={it} onSigned={load} />}
                {canWrite && <button data-testid={`daily-edit-${it.id}`} onClick={() => openEdit(it)} className="text-slate-400 hover:text-blue-700"><PencilSimple size={14} /></button>}
                {canWrite && <button data-testid={`daily-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>}
              </div>
            </div>
            {it.talk_title && (
              <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 text-xs text-slate-700 flex items-center gap-2">
                <span className="font-bold uppercase text-[10px] text-orange-700">Plática:</span>
                <span className="truncate">{it.talk_title}</span>
                <span className="text-slate-500 whitespace-nowrap">· {it.talk_duration_min || 0} min · {it.talk_participants || 0} participantes</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
